// Toast system — global notification primitive.
//
// Consumers call `useToast()` and dispatch via the four type methods:
//   const toast = useToast();
//   toast.info("Saved");
//   toast.success("Invite created", { action: { label: "Copy link", onClick } });
//   toast.warning("Sync delayed", { duration: 10000 });
//   toast.error("Sign-out failed");
//
// Behavior:
//   * Bottom-right stack, newest on top.
//   * Auto-dismiss after 5s (info/success) or 8s (warning/error).
//   * An "Undo" toast stays 60s (Cleanup UX rule 7); other action toasts 8s.
//   * duration: 0 persists only for errors; anything else closes after 60s.
//   * At most three on screen; the oldest one without an Undo goes first.
//   * Close (X) on every toast.
//   * aria-live="polite" for info+success, "assertive" for warning+error.
//   * Reduced-motion users get instant entry/exit (CSS handles).
//
// Pure helpers (defaults, role/aria mapping, id generation) live in
// `toast-helpers.ts` so they can be unit-tested in the node pool
// without a React renderer. The provider + components below are the
// React-runtime layer that consumes them.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

import {
  ariaRoleForToast,
  computeToastDuration,
  toastIdsToDrop,
  newToastId,
  type ToastAction,
  type ToastInput,
  type ToastType,
} from "./toast-helpers";

export type { ToastAction, ToastInput, ToastType };

interface ToastRecord {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
  action: ToastAction | null;
}

export interface ToastApi {
  info: (message: string, opts?: ToastInput) => string;
  success: (message: string, opts?: ToastInput) => string;
  warning: (message: string, opts?: ToastInput) => string;
  error: (message: string, opts?: ToastInput) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  // Hold timer handles per toast so dismiss can clear them.
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timersRef.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timersRef.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (type: ToastType, message: string, opts?: ToastInput) => {
      const id = newToastId();
      const action = opts?.action ?? null;
      const duration = computeToastDuration(type, opts);
      const record: ToastRecord = {
        id,
        type,
        message,
        duration,
        action,
      };
      setToasts((prev) => {
        const next = [record, ...prev];
        const drop = new Set(toastIdsToDrop(next));
        for (const gone of drop) {
          const handle = timersRef.current.get(gone);
          if (handle) {
            clearTimeout(handle);
            timersRef.current.delete(gone);
          }
        }
        return drop.size > 0 ? next.filter((t) => !drop.has(t.id)) : next;
      });
      if (duration > 0) {
        const handle = setTimeout(() => dismiss(id), duration);
        timersRef.current.set(id, handle);
      }
      return id;
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      info: (message, opts) => push("info", message, opts),
      success: (message, opts) => push("success", message, opts),
      warning: (message, opts) => push("warning", message, opts),
      error: (message, opts) => push("error", message, opts),
      dismiss,
    }),
    [push, dismiss],
  );

  // Clean up any pending timers on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const handle of timers.values()) {
        clearTimeout(handle);
      }
      timers.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      "useToast must be called inside a <ToastProvider> tree.",
    );
  }
  return ctx;
}

interface ToastContainerProps {
  toasts: ToastRecord[];
  onDismiss: (id: string) => void;
}

function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  // Two stacks: polite (info, success) and assertive (warning,
  // error) so screen readers announce the high-severity ones via the
  // assertive live region. Visually they share the same screen
  // position; the role/aria-live distinction is what matters.
  const polite = toasts.filter(
    (t) => t.type === "info" || t.type === "success",
  );
  const assertive = toasts.filter(
    (t) => t.type === "warning" || t.type === "error",
  );
  return (
    <div
      className="toast-stack"
      role="region"
      aria-label="Notifications"
      data-testid="toast-stack"
    >
      <div aria-live="polite" aria-atomic="false">
        {polite.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
        ))}
      </div>
      <div aria-live="assertive" aria-atomic="false">
        {assertive.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
        ))}
      </div>
    </div>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastRecord;
  onDismiss: (id: string) => void;
}) {
  const labelId = useId();
  return (
    <div
      className={`toast toast-${toast.type}`}
      role={ariaRoleForToast(toast.type)}
      aria-labelledby={labelId}
      data-testid={`toast-${toast.type}`}
    >
      <div className="toast-body" id={labelId}>
        {toast.message}
      </div>
      {toast.action && (
        <button
          type="button"
          className="toast-action"
          onClick={() => {
            toast.action?.onClick();
            onDismiss(toast.id);
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        className="toast-close"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(toast.id)}
      >
        ×
      </button>
    </div>
  );
}
